import React, { useEffect, useMemo, useState } from 'react';
import { fetchInvoices } from '../models/InvoiceService';
import { fetchTrimestres, getCurrentTrimestre } from '../models/TrimestreService';
import { toDate, computePeriod, RATE_LABEL, URSSAF_URL } from '../models/urssaf';
import PageShell, { Pill, fmtEUR } from '../components/design/PageShell';

const Trimestres = () => {
  const [trimestres, setTrimestres] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [tri, inv] = await Promise.all([fetchTrimestres(), fetchInvoices()]);
        setTrimestres(tri);
        setInvoices(inv);
        const curr = await getCurrentTrimestre();
        if (curr) setOpenId(curr.id);
        else if (tri.length) setOpenId(tri[tri.length - 1].id);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    })();
  }, []);

  const sorted = useMemo(() =>
    [...trimestres].sort((a, b) => (toDate(b.debut) || 0) - (toDate(a.debut) || 0)),
  [trimestres]);

  const calcFor = (t) => {
    const start = toDate(t.debut);
    const end = toDate(t.fin);
    if (!start || !end) return { revenue: 0, urssaf: 0, net: 0, invCount: 0 };
    const p = computePeriod(invoices, start, end);
    return {
      revenue: p.revenue,
      urssaf: p.charges.total,
      net: p.charges.net,
      invCount: p.count,
      start,
      end,
    };
  };

  const open = trimestres.find(t => t.id === openId);
  const openCalc = open ? calcFor(open) : null;

  const monthlyFor = (open) => {
    const s = toDate(open.debut);
    const e = toDate(open.fin);
    if (!s || !e) return [];
    const months = [];
    const cursor = new Date(s.getFullYear(), s.getMonth(), 1);
    while (cursor <= e) {
      const mStart = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
      const mEnd = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0, 23, 59, 59);
      const p = computePeriod(invoices, mStart, mEnd);
      months.push({
        label: mStart.toLocaleDateString('fr-FR', { month: 'short', year: '2-digit' }),
        total: p.revenue,
        n: p.count,
      });
      cursor.setMonth(cursor.getMonth() + 1);
    }
    return months;
  };

  const monthly = open ? monthlyFor(open) : [];
  const maxMonthly = Math.max(1, ...monthly.map(m => m.total));

  return (
    <PageShell
      section="trimestres"
      number="06"
      title="Trimestres"
      subtitle="Déclarations URSSAF · Micro-entreprise"
    >
      {loading && <div className="mono" style={{ color: 'var(--ink-3)' }}>Chargement…</div>}

      {!loading && (
        <div className="ta-trim-layout" style={{ display: 'grid', gridTemplateColumns: '280px 1fr', gap: 56 }}>
          <aside style={{ borderRight: '1px solid var(--rule)', marginRight: -20 }}>
            <div className="eyebrow" style={{ padding: '0 20px 16px' }}>// Historique</div>
            {sorted.map(t => {
              const c = calcFor(t);
              const isOpen = t.id === openId;
              return (
                <button
                  key={t.id}
                  onClick={() => setOpenId(t.id)}
                  style={{
                    display: 'block', textAlign: 'left', width: '100%',
                    padding: 20,
                    borderLeft: `2px solid ${isOpen ? 'var(--accent)' : 'transparent'}`,
                    borderTop: 0, borderRight: 0,
                    borderBottom: '1px solid var(--rule)',
                    background: isOpen ? 'var(--bg-1)' : 'transparent',
                    color: 'inherit', cursor: 'pointer',
                    transition: 'all 0.15s',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span className="mono" style={{ fontSize: 11, letterSpacing: '0.14em', color: isOpen ? 'var(--accent)' : 'var(--ink-3)' }}>
                      {t.titre}
                    </span>
                    {t.declared && <Pill tone="ok">déclaré</Pill>}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 6 }}>
                    {toDate(t.debut)?.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })} → {toDate(t.fin)?.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: '2-digit' })}
                  </div>
                  <div className="mono tnum" style={{ fontSize: 16, marginTop: 12, color: isOpen ? 'var(--ink)' : 'var(--ink-2)' }}>
                    {fmtEUR(c.revenue)} €
                  </div>
                </button>
              );
            })}
          </aside>

          <section>
            {open && openCalc && (
              <>
                <div className="eyebrow">{"// "}Détail trimestre</div>
                <h2 style={{ fontFamily: 'var(--font-mono)', fontWeight: 500, fontSize: 68, letterSpacing: '-0.04em', lineHeight: 0.9, margin: '16px 0 10px' }}>
                  {open.titre}
                </h2>
                <div className="mono" style={{ color: 'var(--ink-3)', fontSize: 12, letterSpacing: '0.08em', marginBottom: 48 }}>
                  {openCalc.start?.toLocaleDateString('fr-FR')} → {openCalc.end?.toLocaleDateString('fr-FR')} · {openCalc.invCount} facture{openCalc.invCount > 1 ? 's' : ''}
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', border: '1px solid var(--rule)' }} className="ta-trim-big">
                  <div style={{ padding: 28, borderRight: '1px solid var(--rule)' }}>
                    <div className="eyebrow">CA encaissé</div>
                    <div className="mono tnum" style={{ fontSize: 32, marginTop: 16, letterSpacing: '-0.01em' }}>{fmtEUR(openCalc.revenue)} €</div>
                  </div>
                  <div style={{ padding: 28, borderRight: '1px solid var(--rule)' }}>
                    <div className="eyebrow">Cotisations URSSAF</div>
                    <div className="mono tnum" style={{ fontSize: 32, marginTop: 16, color: 'var(--warn)', letterSpacing: '-0.01em' }}>{fmtEUR(openCalc.urssaf)} €</div>
                    <div className="mono" style={{ fontSize: 10, color: 'var(--ink-3)', letterSpacing: '0.1em', marginTop: 8 }}>
                      Presta {RATE_LABEL.prestations} · March. {RATE_LABEL.marchandises}
                    </div>
                  </div>
                  <div style={{ padding: 28 }}>
                    <div className="eyebrow">Net après charges</div>
                    <div className="mono tnum" style={{ fontSize: 32, marginTop: 16, color: 'var(--accent)', letterSpacing: '-0.01em' }}>{fmtEUR(openCalc.net)} €</div>
                  </div>
                </div>

                {monthly.length > 0 && (
                  <>
                    <div className="eyebrow" style={{ marginTop: 56, marginBottom: 20 }}>§ 06.A · Répartition mensuelle</div>
                    <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.max(1, monthly.length)}, 1fr)`, gap: 32 }} className="ta-trim-months">
                      {monthly.map((m, i) => (
                        <div key={i} style={{ padding: '20px 0', borderTop: '1px solid var(--rule-strong)' }}>
                          <div className="mono" style={{ fontSize: 10, letterSpacing: '0.14em', color: 'var(--ink-3)', textTransform: 'uppercase' }}>{m.label}</div>
                          <div className="mono tnum" style={{ fontSize: 22, marginTop: 14 }}>{fmtEUR(m.total)} €</div>
                          <div style={{ marginTop: 16, height: 2, background: 'var(--rule)' }}>
                            <div style={{ height: '100%', background: 'var(--accent)', width: `${(m.total / maxMonthly) * 100}%` }} />
                          </div>
                          <div className="mono" style={{ fontSize: 10, color: 'var(--ink-4)', marginTop: 8, letterSpacing: '0.08em' }}>
                            {String(m.n).padStart(2, '0')} FACTURE{m.n > 1 ? 'S' : ''}
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )}

                <div className="eyebrow" style={{ marginTop: 56, marginBottom: 20 }}>§ 06.B · Déclaration</div>
                <div style={{
                  padding: 28,
                  border: '1px solid var(--accent)',
                  background: 'var(--accent-soft)',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: 24,
                  flexWrap: 'wrap',
                }}>
                  <div>
                    <div style={{ fontSize: 20, fontWeight: 500, letterSpacing: '-0.01em' }}>
                      Déclaration URSSAF pour {open.titre}
                    </div>
                    <div className="mono" style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 10, letterSpacing: '0.08em' }}>
                      autoentrepreneur.urssaf.fr · espace personnel
                    </div>
                  </div>
                  <a href={URSSAF_URL} target="_blank" rel="noreferrer" className="ta-btn ta-btn-primary">
                    <span>Déclarer →</span>
                  </a>
                </div>
              </>
            )}
            {!open && <div className="mono" style={{ color: 'var(--ink-3)' }}>Sélectionnez un trimestre</div>}
          </section>
        </div>
      )}

      <style>{`
        @media (max-width: 768px) {
          .ta-trim-layout { grid-template-columns: 1fr !important; gap: 32px !important; }
          .ta-trim-layout aside { margin-right: 0 !important; border-right: 0 !important; border-bottom: 1px solid var(--rule); padding-bottom: 24px; }
          .ta-trim-big { grid-template-columns: 1fr !important; }
          .ta-trim-big > div { border-right: 0 !important; border-bottom: 1px solid var(--rule); }
          .ta-trim-big > div:last-child { border-bottom: 0; }
          .ta-trim-months { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </PageShell>
  );
};

export default Trimestres;
